<!-- 宠物领取/更换记录：家长与管理员操作审计。最后更新：2026-10-07 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" :type="0"></DepartmentPane>
    </section>
    <section class="dp-right pet-log-page">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">当前机构</span>
          <span class="dp-dept-hint__value">{{ departmentName || '未选择' }}</span>
        </p>
      </div>

      <div class="filter-bar">
        <el-select v-model="query.semesterId" size="small" clearable placeholder="全部学期" class="filter-bar__semester" @change="handleQuery">
          <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
        </el-select>
        <el-date-picker
          v-model="query.dateRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :picker-options="rangeShortcuts"
          class="filter-bar__range"
          @change="handleQuery" />
        <el-select v-model="query.actionType" size="small" clearable placeholder="全部操作" class="filter-bar__small" @change="handleQuery">
          <el-option :value="1" label="领取" />
          <el-option :value="2" label="更换" />
        </el-select>
        <el-select v-model="query.operatorType" size="small" clearable placeholder="全部来源" class="filter-bar__small" @change="handleQuery">
          <el-option value="parent" label="家长" />
          <el-option value="admin" label="管理员" />
        </el-select>
        <el-input
          v-model="query.keyword"
          size="small"
          clearable
          placeholder="姓名 / 学号"
          prefix-icon="el-icon-search"
          class="filter-bar__search"
          @keyup.enter.native="handleQuery"
          @clear="handleQuery" />
        <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="handleQuery">查询</el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
        <el-button type="warning" plain size="small" icon="el-icon-download" :loading="exporting" @click="exportLog">导出 Excel</el-button>
      </div>

      <div class="tbl-scroll-host">
        <el-table :data="list" v-loading="loading" height="100%" style="width: 100%" class="log-table">
          <el-table-column label="操作时间" width="150" align="center">
            <template slot-scope="{ row }"><span class="num">{{ formatTime(row.createTime) }}</span></template>
          </el-table-column>
          <el-table-column label="姓名" min-width="100" align="center">
            <template slot-scope="{ row }">
              <el-tooltip placement="top" :open-delay="300" :content="row.studentName || ''" :disabled="!row.studentName">
                <span class="ellipsis strong">{{ row.studentName || '—' }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="学号" min-width="100" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }"><span class="muted">{{ row.studentNo || '—' }}</span></template>
          </el-table-column>
          <el-table-column prop="departmentName" label="班级" min-width="100" align="center" show-overflow-tooltip />
          <el-table-column label="操作" width="80" align="center">
            <template slot-scope="{ row }">
              <span :class="['action-tag', row.actionType === 2 ? 'is-change' : 'is-claim']">{{ row.actionType === 2 ? '更换' : '领取' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="宠物变化" min-width="170" align="center">
            <template slot-scope="{ row }">
              <span v-if="row.actionType === 2" class="pet-flow">
                <span class="muted">{{ row.fromPetName || '已删除' }}</span>
                <i class="el-icon-right"></i>
                <span class="strong">{{ row.toPetName || '已删除' }}</span>
              </span>
              <span v-else class="strong">{{ row.toPetName || '已删除' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="当时等级" min-width="120" align="center">
            <template slot-scope="{ row }">
              <template v-if="row.levelIndex">
                <span class="level-badge">Lv.{{ row.levelIndex }}</span>
                <span class="muted">{{ row.levelName }}</span>
              </template>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="学期分" width="90" align="center">
            <template slot-scope="{ row }">
              <el-tooltip placement="top" :content="row.scoreMode === 'PositiveOnly' ? '计分模式：只计加分' : '计分模式：净分'">
                <span class="num">{{ formatScore(row.semesterScore) }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="当天分" width="80" align="center">
            <template slot-scope="{ row }"><span :class="['num', scoreClass(row.todayScore)]">{{ signedScore(row.todayScore) }}</span></template>
          </el-table-column>
          <el-table-column prop="semesterName" label="学期" min-width="120" align="center" show-overflow-tooltip />
          <el-table-column label="操作人" min-width="110" align="center">
            <template slot-scope="{ row }">
              <span :class="['source-tag', row.operatorType === 'admin' ? 'is-admin' : 'is-parent']">{{ row.operatorType === 'admin' ? '管理员' : '家长' }}</span>
              <span v-if="row.operatorName" class="muted operator-name">{{ row.operatorName }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container">
        <el-pagination
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="page.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange" />
      </div>
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import { getPetChangeLogList, exportPetChangeLogList, getPetSemesterList } from '@/api/modules/pet'
import { resolveActiveSchoolId, treeNodeDeptId, deptIdsEqual } from '@/utils/deptCascaderProps'

/** 日期格式化为 yyyy-MM-dd */
function formatDate (date) {
  const d = new Date(date)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 最近 N 天的日期区间 */
function recentDays (days) {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - (days - 1))
  return [start, end]
}

export default {
  name: 'PetChangeLog',
  components: { DepartmentPane },
  data () {
    return {
      schoolId: 0,
      departmentId: 0,
      departmentName: '',
      loading: false,
      exporting: false,
      list: [],
      semesters: [],
      query: { semesterId: null, dateRange: null, actionType: null, operatorType: '', keyword: '' },
      page: { currentPage: 1, pageSize: 20, total: 0 },
      rangeShortcuts: {
        shortcuts: [
          { text: '今天', onClick: picker => picker.$emit('pick', recentDays(1)) },
          { text: '最近 7 天', onClick: picker => picker.$emit('pick', recentDays(7)) },
          { text: '最近 30 天', onClick: picker => picker.$emit('pick', recentDays(30)) }
        ]
      }
    }
  },
  methods: {
    /** 左侧部门树点击：学校取顶栏当前学校 */
    async treeNodeClick (data) {
      this.departmentId = treeNodeDeptId(data)
      this.departmentName = (data && (data.label || data.Name)) || ''
      const schoolId = resolveActiveSchoolId(this)
      if (schoolId && !deptIdsEqual(schoolId, this.schoolId)) {
        this.schoolId = schoolId
        await this.loadSemesters()
      }
      this.handleQuery()
    },
    async loadSemesters () {
      if (!this.schoolId) return
      const res = await getPetSemesterList({ schoolId: this.schoolId })
      this.semesters = (res.data && res.data.response) || []
      const current = this.semesters.find(x => x.isCurrent)
      this.query.semesterId = current ? current.semesterId : null
    },
    handleQuery () {
      this.page.currentPage = 1
      this.load()
    },
    resetQuery () {
      const current = this.semesters.find(x => x.isCurrent)
      this.query = { semesterId: current ? current.semesterId : null, dateRange: null, actionType: null, operatorType: '', keyword: '' }
      this.handleQuery()
    },
    async load () {
      if (!this.schoolId || !this.departmentId) return
      this.loading = true
      try {
        const range = this.query.dateRange || []
        const res = await getPetChangeLogList({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          startDate: range[0] || null,
          endDate: range[1] || null,
          actionType: this.query.actionType || null,
          operatorType: this.query.operatorType || '',
          keyword: (this.query.keyword || '').trim(),
          page: this.page.currentPage,
          pageSize: this.page.pageSize
        })
        if (res.data && res.data.success) {
          const data = res.data.response || {}
          this.list = data.data || []
          this.page.total = data.dataCount || 0
        } else {
          this.list = []
          this.$message.error((res.data && res.data.msg) || '查询失败')
        }
      } finally {
        this.loading = false
      }
    },
    handleCurrentChange (val) {
      this.page.currentPage = val
      this.load()
    },
    handleSizeChange (val) {
      this.page.pageSize = val
      this.handleQuery()
    },
    /** 导出当前筛选条件下的全部操作记录。 */
    async exportLog () {
      if (!this.schoolId || !this.departmentId) return this.$message.warning('请先在左侧选择部门')
      this.exporting = true
      const range = this.query.dateRange || []
      try {
        const res = await exportPetChangeLogList({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          startDate: range[0] || null,
          endDate: range[1] || null,
          actionType: this.query.actionType || null,
          operatorType: this.query.operatorType || '',
          keyword: (this.query.keyword || '').trim()
        })
        if (!res.data || !res.data.size) return this.$message.error('没有可导出的记录')
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `宠物操作记录_${this.query.dateRange ? this.query.dateRange.join('_') : '全部'}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    },
    formatTime (value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    },
    formatScore (value) {
      if (value === null || value === undefined) return '—'
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      if (value === null || value === undefined) return '—'
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    scoreClass (value) {
      const num = Number(value) || 0
      return num > 0 ? 'is-up' : num < 0 ? 'is-down' : ''
    },
    formatDate
  }
}
</script>

<style scoped>
.pet-log-page { display: flex; flex-direction: column; min-height: 0; }
.page-head { display: flex; align-items: center; margin-bottom: 8px; }
.page-head .dp-dept-hint { margin: 0; }
.filter-bar { flex: 0 0 auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.filter-bar__semester { width: 190px; }
.filter-bar__range { width: 250px !important; }
.filter-bar__small { width: 110px; }
.filter-bar__search { width: 160px; }

.log-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }
.num { font-variant-numeric: tabular-nums; color: #0f172a; }
.num.is-up { color: #15803d; }
.num.is-down { color: #b45309; }
.muted { color: #64748b; font-size: 12px; }
.strong { color: #0f172a; font-weight: 500; }
.ellipsis { display: inline-block; max-width: 100%; vertical-align: middle; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default; }
.action-tag, .source-tag { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.action-tag.is-claim { background: #dcfce7; color: #15803d; }
.action-tag.is-change { background: #eff6ff; color: #1d4ed8; }
.source-tag.is-parent { background: #fef3c7; color: #b45309; }
.source-tag.is-admin { background: #ede9fe; color: #6d28d9; }
.operator-name { margin-left: 6px; }
.pet-flow { display: inline-flex; align-items: center; gap: 6px; }
.pet-flow i { color: #94a3b8; }
.level-badge { display: inline-block; padding: 1px 7px; margin-right: 6px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 600; }
</style>
